import { Dialog, DialogPanel } from '@headlessui/react';
import { JxtTagColorsEnum } from '@jooxter/utils';
import { clsx } from 'clsx';
import JxtHomeSkeleton from '../JxtHomeSkeleton';
import { availabilityToneClasses } from '../JxtHomeSpaceCard/styles';
import { JxtResourceIconInfo } from '../JxtResourceIcon/constants';
import { IJxtSpaceModal } from './types';

const ICON_TONE: Record<JxtTagColorsEnum, string> = {
  [JxtTagColorsEnum.GREEN]: 'bg-green-10 text-green-140',
  [JxtTagColorsEnum.ORANGE]: 'bg-orange-10 text-orange-140',
  [JxtTagColorsEnum.RED]: 'bg-red-10 text-red-140',
  [JxtTagColorsEnum.BLUE]: 'bg-primary-10 text-primary-140',
  [JxtTagColorsEnum.NEUTRAL]: 'bg-neutral-20 text-neutral-110',
};

/**
 * What opens on one space: who it is, its state and its tabs stay on top, the tab's content scrolls
 * under them. Centered, a panel from the bottom on a phone.
 */
const JxtSpaceModal = ({
  open,
  onClose,
  closeLabel,
  isLoading = false,
  name,
  meta,
  metaType,
  status,
  favorite,
  nameAction,
  tabsLabel,
  tabs,
  activeTab,
  onTabChange,
  children,
}: IJxtSpaceModal) => (
  // z-30, as JxtModal: what it opens (a booking's move, a confirmation) comes above it.
  <Dialog open={open} onClose={onClose} className="relative z-30">
    <div className="fixed inset-0 bg-neutral-150/60 backdrop-blur-sm" aria-hidden />
    <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-4">
      <DialogPanel
        aria-label={name || tabsLabel}
        aria-busy={isLoading}
        className="flex max-h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-xl bg-white text-left shadow-elevation-3 sm:max-h-[88vh] sm:rounded-xl"
      >
        <header className="shrink-0 px-5 pt-5 pb-4 sm:px-6">
          <div className="flex items-start gap-3">
            {isLoading ? (
              <>
                <JxtHomeSkeleton className="size-12 shrink-0 rounded-xl" />
                <div className="flex min-w-0 flex-1 flex-col gap-2 py-0.5">
                  <JxtHomeSkeleton className="h-5 w-48 max-w-full rounded-md" />
                  <JxtHomeSkeleton className="h-4 w-72 max-w-full rounded-md" />
                </div>
              </>
            ) : (
              <>
                <span
                  className={clsx(
                    'flex size-12 shrink-0 items-center justify-center rounded-xl text-title-s',
                    ICON_TONE[status?.tone ?? JxtTagColorsEnum.NEUTRAL]
                  )}
                >
                  {metaType && <i className={clsx('fas', JxtResourceIconInfo[metaType])} aria-hidden />}
                </span>
                <div className="min-w-0 flex-1">
                  <h2 className="flex items-center gap-2 text-title-m font-semibold text-neutral-140">
                    <span className="truncate">{name}</span>
                    {favorite}

                    {nameAction && (
                      <button
                        type="button"

                        onClick={nameAction.onClick}

                        className="shrink-0 text-body-xs font-semibold text-primary-110 hover:text-primary-140"
                      >
                        {nameAction.label}
                      </button>
                    )}
                  </h2>
                  {meta && <p className="text-body-s text-neutral-100">{meta}</p>}
                  {status && (
                    <p
                      className={clsx(
                        'mt-1 flex items-center gap-1.5 text-body-s font-semibold',
                        availabilityToneClasses[status.tone].text
                      )}
                    >
                      <span
                        className={clsx('size-2 shrink-0 rounded-full', availabilityToneClasses[status.tone].dot)}
                      />
                      {status.label}
                    </p>
                  )}
                </div>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="-mt-1 -mr-2 flex size-10 shrink-0 items-center justify-center rounded-lg text-neutral-90 hover:bg-neutral-10 hover:text-neutral-140"
            >
              <span className="sr-only">{closeLabel}</span>
              <i className="fas fa-xmark text-lg" aria-hidden />
            </button>
          </div>
          {tabs.length > 0 && (
            <div
              role="tablist"
              aria-label={tabsLabel}
              className="mt-4 flex rounded-lg bg-neutral-10 p-1 sm:inline-flex"
            >
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={tab.id === activeTab}
                  onClick={() => onTabChange(tab.id)}
                  className="inline-flex items-center justify-center gap-2 rounded-md px-2 py-1.5 text-body-s font-semibold text-neutral-100 hover:text-neutral-140 aria-selected:bg-white aria-selected:text-neutral-140 max-sm:flex-1 sm:px-4"
                >
                  <i className={clsx('fas text-body-xs', `fa-${tab.icon}`)} aria-hidden />
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </header>
        <div role="tabpanel" className="flex min-h-0 flex-1 flex-col overflow-y-auto border-t border-neutral-10">
          {children}
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);

export default JxtSpaceModal;
